import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, 
  AlertTriangle, 
  Calendar, 
  MapPin, 
  FileText, 
  MessageCircle,
  Coins,
  Clock,
  CheckCircle,
  XCircle,
  HelpCircle,
  Reply,
  Lock,
  Sparkles
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { AdminPagination } from '@/components/admin/AdminPagination';
import { useJobProposals, useJobProposalCounts, JobProposalWithDetails } from '@/hooks/useJobProposals';
import { useBusiness } from '@/contexts/BusinessContext';
import { useBusinessDiscountForManagement } from '@/hooks/useBusinessDiscount';
import { supabase } from '@/integrations/supabase/client';
import { getUrgencyDisplay as getUrgencyDisplayUtil } from '@communitly/shared';

const MyJobOpportunities = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { userBusinessId } = useBusiness();
  const { data: businessDiscount } = useBusinessDiscountForManagement(userBusinessId);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasSmartJobMatching, setHasSmartJobMatching] = useState<boolean | null>(null);
  const [checkingFeature, setCheckingFeature] = useState(true);
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  
  // Initialize state with URL parameters
  const [activeTab, setActiveTab] = useState(tabFromUrl || "all");

  // Update URL when state changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    
    // Handle tab parameter
    if (activeTab === "all") {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', activeTab);
    }
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString()
    }, { replace: true });
  }, [activeTab, location.pathname, location.search, navigate]);

  // Fetch job proposals
  const { data: proposalsResponse, isLoading, error } = useJobProposals({
    searchTerm,
    status: activeTab,
    currentPage,
    pageSize
  });

  // Fetch status counts
  const { data: statusCounts } = useJobProposalCounts();

  // Determine if we should show restricted view (paid business without subscription)
  const shouldShowRestricted = businessDiscount?.type !== 'Free' && hasSmartJobMatching === false;

  // Check if business has smart_job_matching feature
  useEffect(() => {
    const checkFeature = async () => {
      if (!userBusinessId) {
        setCheckingFeature(false);
        return;
      }

      setCheckingFeature(true);
      try {
        const { data, error } = await supabase.rpc('has_feature', {
          business_id: userBusinessId,
          feature_key: 'smart_job_matching'
        });

        if (error) {
          console.error('Error checking feature:', error);
          setHasSmartJobMatching(false);
        } else {
          setHasSmartJobMatching(data);
        }
      } catch (error) {
        console.error('Error checking feature:', error);
        setHasSmartJobMatching(false);
      } finally {
        setCheckingFeature(false);
      }
    };

    checkFeature();
  }, [userBusinessId]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, pageSize]);

  const proposals = proposalsResponse?.proposals || [];
  const totalCount = proposalsResponse?.totalCount || 0;
  const totalPages = proposalsResponse?.totalPages || 0;

  const handleProposalClick = (proposalId: string) => {
    navigate(`/admin/my-job-opportunities/${proposalId}`);
  };

  const handleReplyClick = (proposalId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Check if has subscription for replying
    if (!hasSmartJobMatching) {
      toast({
        title: "Subscription Required",
        description: "You need an active subscription to respond to job opportunities.",
        variant: "destructive",
      });
      return;
    }
    
    navigate(`/admin/my-job-opportunities/${proposalId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'AWAITING_RESPONSE':
        return {
          icon: Clock,
          label: 'Awaiting Response',
          className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
          color: 'text-yellow-600'
        };
      case 'PROPOSED':
        return {
          icon: CheckCircle,
          label: 'Proposed',
          className: 'bg-green-50 text-green-700 border-green-200',
          color: 'text-green-600'
        };
      case 'DECLINED':
        return {
          icon: XCircle,
          label: 'Declined',
          className: 'bg-red-50 text-red-700 border-red-200',
          color: 'text-red-600'
        };
      case 'REQUESTED_INFO':
        return {
          icon: HelpCircle,
          label: 'Info Requested',
          className: 'bg-blue-50 text-blue-700 border-blue-200',
          color: 'text-blue-600'
        };
      default:
        return {
          icon: Clock,
          label: status,
          className: 'bg-gray-50 text-gray-700 border-gray-200',
          color: 'text-gray-600'
        };
    }
  };

  // Use shared utility with translation support
  const getUrgencyDisplay = (urgency: string) => getUrgencyDisplayUtil(urgency, t);

  const getProposalSummary = (proposal: JobProposalWithDetails) => {
    const hasQuote = proposal.quote !== null || proposal.detailed_quote !== null;
    const hasResponse = proposal.response_message !== null && proposal.response_message.trim() !== '';
    
    return {
      hasQuote,
      hasResponse,
      quoteAmount: proposal.detailed_quote?.fixed_price || proposal.detailed_quote?.estimated_price || proposal.quote,
      quoteType: proposal.detailed_quote?.price_type || (proposal.quote ? 'fixed' : null)
    };
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
        <div className="relative">
          <h1 className="admin-section-title mb-2">My Job Opportunities</h1>
          <p className="text-muted-foreground/80 text-lg">
            Manage job opportunities from community members seeking your services.
          </p>
        </div>


        <Card className="admin-card admin-card-stats">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 pb-6">
            <div>
              <CardTitle className="text-xl font-bold text-slate-800">Job Opportunities</CardTitle>
              <CardDescription className="text-muted-foreground/80 mt-2">
                View and respond to service requests from community members
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search opportunities..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="flex space-x-2">
                  <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                  <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                  <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-destructive">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                <p>Error loading job opportunities</p>
              </div>
            ) : (
              <div className={`relative ${shouldShowRestricted ? 'min-h-[400px]' : ''}`}>
                {/* Blur effect for restricted content */}
                <div className={shouldShowRestricted ? 'blur-restricted' : ''}>
                  <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="mb-4">
                    <TabsList className="w-full md:w-auto grid grid-cols-2 md:flex md:max-w-full h-auto md:h-10 gap-1 md:gap-0 p-1">
                      <TabsTrigger value="all" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                        All ({statusCounts?.all || 0})
                      </TabsTrigger>
                      <TabsTrigger value="AWAITING_RESPONSE" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                        Awaiting ({statusCounts?.AWAITING_RESPONSE || 0})
                      </TabsTrigger>
                      <TabsTrigger value="PROPOSED" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                        Proposed ({statusCounts?.PROPOSED || 0})
                      </TabsTrigger>
                      <TabsTrigger value="DECLINED" className="w-full md:flex-1 text-xs md:text-sm px-1 py-3 md:px-3 md:py-2 text-center leading-tight">
                        Declined ({statusCounts?.DECLINED || 0})
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  
                  {/* Mobile Cards */}
                  <div className="block md:hidden space-y-4">
                    {proposals && proposals.length > 0 ? (
                      proposals.map((proposal, index) => {
                        const statusDisplay = getStatusDisplay(proposal.status);
                        const summary = getProposalSummary(proposal);
                        const StatusIcon = statusDisplay.icon;
                        
                        return (
                          <Card 
                            key={proposal.id} 
                            className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)}`}
                            onClick={() => handleProposalClick(proposal.id)}
                          >
                          <CardContent className="p-5 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                              <div className="flex-1 space-y-2">
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-muted-foreground">User request</p>
                                  <div className="border-l-4 border-gray-300 pl-3">
                                    <p className="text-sm italic text-gray-700 line-clamp-2">
                                      {proposal.service_request.original_request}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="self-start">
                                <Badge variant="outline" className={statusDisplay.className}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusDisplay.label}
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 flex-shrink-0" />
                                <span>{formatDate(proposal.created_at)}</span>
                              </div>
                              <div>
                                <Badge variant="outline" className={`text-xs ${getUrgencyDisplay(proposal.service_request.urgency).className}`}>
                                  {getUrgencyDisplay(proposal.service_request.urgency).label}
                                </Badge>
                              </div>
                            </div>

                            {/* Quote/Response indicators and Town */}
                            <div className="flex items-center justify-between pt-2">
                              <div className="flex flex-wrap items-center gap-3 text-xs">
                                {summary.hasQuote && (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <Coins className="h-3 w-3 flex-shrink-0" />
                                    <span>Quote provided</span>
                                  </div>
                                )}
                                {summary.hasResponse && (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <MessageCircle className="h-3 w-3 flex-shrink-0" />
                                    <span>Response sent</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Town in bottom right */}
                              {proposal.service_request.town && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <MapPin className="h-3 w-3 flex-shrink-0" />
                                  <span>{proposal.service_request.town}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Action buttons based on proposal status */}
                            <div className="mt-3 pt-3 border-t flex justify-end" onClick={(e) => e.stopPropagation()}>
                              {proposal.status === 'AWAITING_RESPONSE' ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className={`gap-2 ${hasSmartJobMatching ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground'}`}
                                  onClick={(e) => handleReplyClick(proposal.id, e)}
                                  disabled={!hasSmartJobMatching}
                                >
                                  {hasSmartJobMatching ? <Reply className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                                  {hasSmartJobMatching ? 'Reply' : 'Upgrade to Reply'}
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="gap-2 text-muted-foreground hover:bg-gray-50"
                                  onClick={(e) => handleReplyClick(proposal.id, e)}
                                >
                                  <FileText className="h-3 w-3" />
                                  View Response
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      {searchTerm ? (
                        <p className="text-muted-foreground">No opportunities match your search.</p>
                      ) : (
                        <p className="text-muted-foreground">No job opportunities yet.</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Desktop Table */}
                <div className="hidden md:block rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-100 bg-gray-50/50">
                        <TableHead className="font-semibold text-slate-700 h-12">Request</TableHead>
                        <TableHead className="font-semibold text-slate-700">Location</TableHead>
                        <TableHead className="font-semibold text-slate-700">Urgency</TableHead>
                        <TableHead className="font-semibold text-slate-700">Status</TableHead>
                        <TableHead className="font-semibold text-slate-700">Quote</TableHead>
                        <TableHead className="font-semibold text-slate-700">Created</TableHead>
                        <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proposals && proposals.length > 0 ? (
                        proposals.map((proposal) => {
                          const statusDisplay = getStatusDisplay(proposal.status);
                          const summary = getProposalSummary(proposal);
                          const StatusIcon = statusDisplay.icon;
                          
                          return (
                            <TableRow 
                              key={proposal.id} 
                              className="cursor-pointer hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors"
                              onClick={() => handleProposalClick(proposal.id)}
                            >
                              <TableCell className="max-w-xs">
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-muted-foreground">User request</p>
                                  <div className="border-l-4 border-gray-300 pl-3">
                                    <p className="text-sm italic text-gray-700 line-clamp-2">
                                      {proposal.service_request.original_request}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {proposal.service_request.town || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={getUrgencyDisplay(proposal.service_request.urgency).className}>
                                  {getUrgencyDisplay(proposal.service_request.urgency).label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={statusDisplay.className}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusDisplay.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {summary.hasQuote ? (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <Coins className="w-4 h-4" />
                                    <span className="text-sm">Provided</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">None</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(proposal.created_at)}
                              </TableCell>
                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-end gap-2">
                                  {proposal.status === 'AWAITING_RESPONSE' ? (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className={`gap-2 ${hasSmartJobMatching ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground'}`}
                                      onClick={(e) => handleReplyClick(proposal.id, e)}
                                      disabled={!hasSmartJobMatching}
                                    >
                                      {hasSmartJobMatching ? <Reply className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                                      {hasSmartJobMatching ? 'Reply' : 'Upgrade to Reply'}
                                    </Button>
                                  ) : (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="gap-2 text-muted-foreground hover:bg-gray-50"
                                      onClick={(e) => handleReplyClick(proposal.id, e)}
                                    >
                                      <FileText className="h-3 w-3" />
                                      View Response
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8">
                            {searchTerm ? (
                              <div>
                                <p className="text-muted-foreground">No opportunities match your search.</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-muted-foreground">No job opportunities have been received yet.</p>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </div>
                </div>
                
                {/* Overlay for restricted access */}
                {shouldShowRestricted && (
                  <div className="blur-overlay">
                    <div className="text-center space-y-3 w-full max-w-sm mx-auto">
                      <div className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        <h3 className="text-lg sm:text-xl font-bold mb-2">
                          {(statusCounts?.all || 0) > 0 
                            ? `${statusCounts?.all || 0} Job Opportunities Waiting!`
                            : 'Ready to Connect with Customers!'
                          }
                        </h3>
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                        {(statusCounts?.all || 0) > 0 
                          ? 'Activate your subscription to respond to job opportunities and grow your business.'
                          : 'Activate your subscription to respond to job opportunities from community members.'
                        }
                      </p>
                      <Button
                        onClick={() => navigate('/admin/business/subscription')}
                        size="sm"
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white gap-2 w-full sm:w-auto text-xs sm:text-sm"
                      >
                        <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                        Unlock Business
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6">
              <AdminPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </CardContent>
        </Card>
    </div>
  );
};

export default MyJobOpportunities;